/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IComponentStyles } from '@fluentui/foundation-legacy';
import { ActionButton, IButtonProps, IButtonStyles } from '@fluentui/react/lib/Button';
import { IContextualMenuItem } from '@fluentui/react/lib/ContextualMenu';
import { ScrollablePane } from '@fluentui/react/lib/ScrollablePane';
import { IStackProps, IStackSlots, Stack } from '@fluentui/react/lib/Stack';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import { Toggle } from '@fluentui/react/lib/Toggle';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { CluTestModelResults } from 'src/clu/core/services/test/types';
import { getConnectionReadableName } from 'src/clu/modules/utils/orchestrationUtils';
import { LuisText } from 'src/core/components/LuisText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { transformLabelToAnnotation } from 'src/core/utils/labelingUtils';
import { numberToPercentageString } from 'src/core/utils/number';
import { commonDemosMessages } from 'src/languageStudio/appShell/prebuiltDemos/utils/commonDemoMessages';
import { resultCardStyles, resultCardTitleStyles } from 'src/languageStudio/appShell/prebuiltDemos/utils/commonStyles';
import { NamedEntity } from 'src/languageStudio/core/services/textAnalytics/textAnalyticsSwaggers';
import {
    EntityCategory,
    SortTypes,
    getEntitiesSortOptions,
    getEntityColor,
    getEntityId,
    getFilterCategories,
    sortEntitiesHelper
} from 'src/languageStudio/core/utils/demosUtils';
import { ModelType } from 'src/luis/types/ModelType';
import { Label } from 'src/modules/labeling/types/Label';
import { calculateRenderingLevel } from 'src/modules/renderingUtils/renderingUtils';
import { AnnotationData, Labeler } from 'src/packages/react-text-annotator/lib/esm';

type ResultToken = { id: string; tokens: { startTokenIndex: number; endTokenIndex: number } };
export type VisualizedCluResultProps = {
    document: string;
    isLabelerRtl: boolean;
    isLabelerWordTokenized: boolean;
    testResult: CluTestModelResults;
};

const messages = defineMessages({
    sort: { id: 'clu.visualizedCluResult.sort', defaultMessage: 'Sort' },
    filter: { id: 'clu.visualizedCluResult.filter', defaultMessage: 'Filter' },
    typeLabel: { id: 'clu.visualizedCluResult.typeLabel', defaultMessage: 'Type:' },
    intentTitle: { id: 'clu.visualizedCluResult.intentTitle', defaultMessage: 'Intent' },
    topAnswer: { id: 'clu.visualizedCluResult.topAnswer', defaultMessage: 'Top Answer' },
    answerTitle: { id: 'clu.visualizedCluResult.answerTitle', defaultMessage: 'Answer' },
    entityTitle: { id: 'clu.visualizedCluResult.entityTitle', defaultMessage: 'Entities' },
    topIntentTitle: { id: 'clu.visualizedCluResult.topIntentTitle', defaultMessage: 'Top intent' },
    longAnswerLabel: { id: 'clu.visualizedCluResult.longAnswerLabel', defaultMessage: 'Long answer:' },
    entityValueLabel: { id: 'clu.visualizedCluResult.entityValueLabel', defaultMessage: 'Entity value:' },
    showEntitiesCardsLabel: { id: 'clu.visualizedCluResult.showEntitiesCardsLabel', defaultMessage: 'Show entities cards' },
    linkedTopIntentLabel: { id: 'clu.visualizedCluResult.linkedTopIntentLabel', defaultMessage: '{intentName} top intent:' },
    hideCardsMessage: { id: 'clu.visualizedCluResult.hideCardsMessage', defaultMessage: 'Turn on cards to see entities cards' },
    noEntities: { id: 'clu.visualizedCluResult.noEntities', defaultMessage: 'No entities predicted' }
});

const theme = getScopedTheme('VisualizedCluResults');
const contextualMenuButtonStyles = classNamesFunction<IButtonProps, IButtonStyles>()({ menuIcon: { display: 'none' } });
const entityCardStyles = classNamesFunction<IStackProps, IComponentStyles<IStackSlots>>()({
    root: { maxWidth: 'fit-content', minHeight: '110px' }
});
const headerStyles = classNamesFunction<IStackProps, IComponentStyles<IStackSlots>>()({
    root: { borderBottom: `1px solid ${theme.borderColor}` }
});
const intentCardStyles = classNamesFunction<IStackProps, IComponentStyles<IStackSlots>>()({
    root: { maxWidth: 'fit-content', minHeight: '110px' }
});

export const getLabel = (entity: NamedEntity<string>, tokens: ResultToken[]): Label => {
    const id = getEntityId(entity);
    const eTokens = tokens.find(t => t.id === id).tokens;

    return {
        depth: 1,
        renderingLevel: 0,
        renderedAs: 'line',
        end: eTokens.endTokenIndex,
        start: eTokens.startTokenIndex,
        intrinsicEnd: eTokens.endTokenIndex,
        intrinsicStart: eTokens.startTokenIndex,
        entity: { id, name: entity.category, roles: [], typeId: ModelType.Simple }
    };
};

export const VisualizedCluResult = (props: VisualizedCluResultProps) => {
    const { testResult, document, isLabelerRtl, isLabelerWordTokenized } = props;
    const { localize } = useLocalization();

    const [showEntitiesCards, setShowEntitiesCards] = React.useState(true);
    const [selectedFilters, setSelectedFilters] = React.useState<string[]>([]);
    const [selectedSortType, setSelectedSortType] = React.useState<SortTypes>('appearanceAsc');

    const { answer, topIntent, topLinkedIntent } = testResult;
    const entities = testResult.entities ?? [];

    let annotations: AnnotationData[] = [];

    const sortMenuItems: IContextualMenuItem[] = getEntitiesSortOptions(selectedSortType, setSelectedSortType);
    const filtersMenuItems: IContextualMenuItem[] = React.useMemo(
        () => getFilterCategories<NamedEntity<string>>(entities, 'category', selectedFilters, setSelectedFilters),
        [entities, selectedFilters]
    );

    const intentCard = (
        <Stack
            key={topIntent.name}
            tokens={{ childrenGap: 7 }}
            data-automation-id={topIntent.name}
            styles={mergeStyleSets(resultCardStyles, intentCardStyles)}>
            <LuisText fontWeight={500} styles={resultCardTitleStyles(getEntityColor<string>(topIntent.name))}>
                {localize(messages.topIntentTitle)}
            </LuisText>
            <Stack>
                <LuisText fontWeight={500} variant="smallPlus">
                    {topIntent.name}
                </LuisText>
                {topIntent.orchestrationType && (
                    <LuisText variant="smallPlus">
                        <LuisText variant="smallPlus" fontWeight={500}>
                            {localize(messages.typeLabel)}
                        </LuisText>
                        &nbsp;
                        {getConnectionReadableName(topIntent.orchestrationType)}
                    </LuisText>
                )}
                <LuisText variant="smallPlus">
                    <LuisText variant="smallPlus" fontWeight={500}>
                        {localize(commonDemosMessages.confidenceLabel)}
                    </LuisText>
                    &nbsp;
                    {numberToPercentageString({ ratio: topIntent.confidenceScore })}
                </LuisText>
                {topLinkedIntent && (
                    <LuisText variant="smallPlus">
                        <LuisText fontWeight={500} variant="smallPlus">
                            {localize(messages.linkedTopIntentLabel, { intentName: topIntent.name })}
                        </LuisText>
                        &nbsp;
                        {topLinkedIntent.name}
                    </LuisText>
                )}
            </Stack>
        </Stack>
    );

    const entityCard = (entity: NamedEntity<string>) => (
        <Stack
            key={getEntityId(entity)}
            tokens={{ childrenGap: 7 }}
            data-automation-id={getEntityId(entity)}
            styles={mergeStyleSets(resultCardStyles, entityCardStyles)}>
            <LuisText fontWeight={500} styles={resultCardTitleStyles(getEntityColor<string>(entity.category))}>
                {entity.category}
            </LuisText>
            <Stack>
                <LuisText variant="smallPlus">{entity.text}</LuisText>
                <LuisText variant="smallPlus">
                    {localize(commonDemosMessages.confidenceLabel)} {numberToPercentageString({ ratio: entity.confidenceScore })}
                </LuisText>
            </Stack>
        </Stack>
    );

    const topAnswerCard = answer && (
        <Stack tokens={{ childrenGap: 7 }} styles={resultCardStyles}>
            <LuisText fontWeight={500} styles={resultCardTitleStyles(theme.answerColor)}>
                {localize(messages.topAnswer)}
            </LuisText>
            <Stack>
                <LuisText variant="smallPlus" fontWeight={600}>
                    {localize(messages.longAnswerLabel)} <LuisText variant="smallPlus">{answer.answer}</LuisText>
                </LuisText>
                <LuisText variant="smallPlus" fontWeight={600}>
                    {localize(commonDemosMessages.confidenceLabel)}
                    &nbsp;
                    <LuisText variant="smallPlus">{numberToPercentageString({ ratio: answer.confidenceScore })}</LuisText>
                </LuisText>
            </Stack>
        </Stack>
    );

    const renderEntities = () => {
        if (!showEntitiesCards) {
            return (
                <LuisText data-automation-id="hiddenCardsMessage" color={theme.secondaryTextColor}>
                    {localize(messages.hideCardsMessage)}
                </LuisText>
            );
        }

        if (!entities.length) {
            return <LuisText color={theme.secondaryTextColor}>{localize(messages.noEntities)}</LuisText>;
        }

        return entities
            .filter(e => selectedFilters.length === 0 || selectedFilters.some(c => c === e.category))
            .sort((a, b) => sortEntitiesHelper<string>(a, b, selectedSortType))
            .map(entityCard);
    };

    if (topIntent.orchestrationType !== 'QuestionAnswering') {
        const resultTokens = entities.map(e => ({
            id: getEntityId(e),
            tokens: { startTokenIndex: e.offset, endTokenIndex: e.offset + e.length - 1 }
        }));
        annotations = calculateRenderingLevel(
            entities
                .map(e => getLabel(e, resultTokens))
                .map(label =>
                    transformLabelToAnnotation(label, entityCard(entities.find(e => label.entity.id === getEntityId(e))), l =>
                        getEntityColor(l.entity.name as EntityCategory)
                    )
                )
        );
    } else {
        annotations = [];
    }

    const disableEntitiesButtons = () => !showEntitiesCards || !entities.length;

    return (
        <Stack styles={{ root: { width: '100%' } }} verticalFill grow verticalAlign="end" data-automation-id="cluResults">
            {topIntent.orchestrationType !== 'QuestionAnswering' && (
                <Stack horizontal horizontalAlign="end" verticalAlign="center" styles={headerStyles}>
                    <Toggle
                        inlineLabel
                        checked={showEntitiesCards}
                        styles={{ root: { marginBottom: 0 } }}
                        label={localize(messages.showEntitiesCardsLabel)}
                        onChange={(_, checked) => setShowEntitiesCards(checked)}
                    />
                    <ActionButton
                        text={localize(messages.sort)}
                        disabled={disableEntitiesButtons()}
                        menuProps={{ items: sortMenuItems }}
                        iconProps={{ iconName: 'Ascending' }}
                    />
                    <ActionButton
                        text={localize(messages.filter)}
                        iconProps={{ iconName: 'Filter' }}
                        disabled={disableEntitiesButtons()}
                        styles={contextualMenuButtonStyles}
                        menuProps={{ items: filtersMenuItems }}
                    />
                </Stack>
            )}

            <Stack verticalFill grow styles={{ root: { position: 'relative' } }}>
                <ScrollablePane styles={{ contentContainer: { overflowX: 'hidden' } }}>
                    <Stack tokens={{ childrenGap: 25, padding: '15px 20px 30px 20px' }}>
                        <LuisText fontWeight={500}>{localize(messages.intentTitle)}</LuisText>
                        {intentCard}

                        {answer && [<LuisText fontWeight={500}>{localize(messages.answerTitle)}</LuisText>, topAnswerCard]}

                        {topIntent.orchestrationType !== 'QuestionAnswering' && [
                            <LuisText fontWeight={500}>{localize(messages.entityTitle)}</LuisText>,
                            <Stack wrap horizontal tokens={{ childrenGap: 20 }}>
                                {renderEntities()}
                            </Stack>
                        ]}

                        <Stack tokens={{ childrenGap: 10 }}>
                            <LuisText fontWeight={500}>{localize(commonDemosMessages.originalText)}</LuisText>
                            <Labeler
                                text={document}
                                labelerHeight="unset"
                                labelerOverflow="unset"
                                annotations={annotations}
                                labelerConfigs={{
                                    isDisabled: true,
                                    isRtl: isLabelerRtl,
                                    tokenizationType: isLabelerWordTokenized ? 'word' : 'character'
                                }}
                            />
                        </Stack>
                    </Stack>
                </ScrollablePane>
            </Stack>
        </Stack>
    );
};
